import { ArrowRight, CheckCircle2, PackageCheck, Settings2, ShieldCheck, Truck } from "lucide-react";
import BobbinImage from "../assests/logo.png";
import StoreImage from "../assests/store.png";
import CmsEditableImage from "../components/cms/CmsEditableImage";
import CmsEditableList from "../components/cms/CmsEditableList";
import CmsEditableText from "../components/cms/CmsEditableText";
import CmsEditBar from "../components/cms/CmsEditBar";
import { useCmsPage } from "../hooks/useCmsPage";

const strengths = [
  {
    title: "Industrial Sewing Parts",
    text: "We supply bobbins, machine parts, and production essentials for shops that need dependable replacement components.",
    icon: Settings2,
  },
  {
    title: "Reliable Fulfillment",
    text: "Orders are handled with a practical focus on availability, fitment, and quick communication when details need confirmation.",
    icon: Truck,
  },
  {
    title: "Quality First",
    text: "Our catalog is built around parts that support clean output, consistent operation, and long-term production use.",
    icon: ShieldCheck,
  },
];

const values = [
  "Clear product information for confident ordering",
  "Support for common industrial sewing machine part needs",
  "Straightforward service for individual buyers and production teams",
  "A growing catalog focused on practical, in-demand components",
];

function AboutUs() {
  const fallback = {
    hero: {
      eyebrow: "About Us",
      headline: "Bobbin USA keeps sewing production moving",
      intro:
        "Bobbin USA is focused on industrial sewing parts, bobbins, and machine components for customers who need practical supply, dependable service, and parts that are ready for real shop use.",
      primaryCtaLabel: "Shop products",
      primaryCtaHref: "/all-products",
      secondaryCtaLabel: "Contact us",
      secondaryCtaHref: "/contact",
      heroImage: "",
    },
    "what-we-do": {
      eyebrow: "What We Do",
      headline: "Practical parts support for industrial sewing needs",
      body:
        "From everyday replacement parts to specialized machine components, we help customers find the right items without unnecessary friction. Our goal is simple: keep ordering clear, responsive, and useful.",
      image: "",
    },
  };
  const cms = useCmsPage("about", fallback);
  const cmsStrengths = cms.json("strengths", "items", strengths);
  const cmsValues = cms.json("what-we-do", "values", values);
  const defaultStrengthIcons = [Settings2, Truck, ShieldCheck];
  const iconMap = { settings: Settings2, truck: Truck, shield: ShieldCheck };

  return (
    <section className="bg-[#f8fbff] text-slate-900">
      <CmsEditBar editMode={cms.editMode} pageTitle="About Page" />
      <div className="bg-white px-4 py-12 sm:px-8 lg:py-16">
        <div className="container">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <CmsEditableText as="p" className="text-sm font-semibold uppercase tracking-[0.18em] text-[#3991D5]" editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="hero" value={cms.text("hero", "eyebrow", fallback.hero.eyebrow)} />
              <CmsEditableText as="h1" className="mt-3 max-w-3xl font-gopher text-4xl font-black uppercase leading-[0.95] text-slate-950 sm:text-6xl" editMode={cms.editMode} fieldKey="headline" multiline onSave={cms.updateField} sectionKey="hero" value={cms.text("hero", "headline", fallback.hero.headline)} />
              <CmsEditableText as="p" className="mt-5 max-w-2xl font-pmesans text-base leading-7 text-slate-600 sm:text-lg" editMode={cms.editMode} fieldKey="intro" multiline onSave={cms.updateField} sectionKey="hero" value={cms.text("hero", "intro", fallback.hero.intro)} />

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  className="inline-flex items-center gap-2 rounded-full bg-[#3991D5] px-6 py-3 font-gopher text-sm font-bold uppercase tracking-wide text-white transition hover:bg-[#1a6db5]"
                  href={cms.text("hero", "primaryCtaHref", fallback.hero.primaryCtaHref)}
                >
                  <CmsEditableText editMode={cms.editMode} fieldKey="primaryCtaLabel" onSave={cms.updateField} sectionKey="hero" value={cms.text("hero", "primaryCtaLabel", fallback.hero.primaryCtaLabel)} />
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  className="inline-flex items-center gap-2 rounded-full border border-[#3991D5]/20 bg-white px-6 py-3 font-gopher text-sm font-bold uppercase tracking-wide text-[#1278b5] transition hover:border-[#3991D5]/40"
                  href={cms.text("hero", "secondaryCtaHref", fallback.hero.secondaryCtaHref)}
                >
                  <CmsEditableText editMode={cms.editMode} fieldKey="secondaryCtaLabel" onSave={cms.updateField} sectionKey="hero" value={cms.text("hero", "secondaryCtaLabel", fallback.hero.secondaryCtaLabel)} />
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden rounded-lg border border-[#3991D5]/10 bg-[#edf7ff] p-6 shadow-[0_18px_60px_rgba(57,145,213,0.12)]">
                <CmsEditableImage
                  alt="Bobbin USA sewing supplies"
                  className="mx-auto h-auto max-h-[380px] w-full object-contain"
                  editMode={cms.editMode}
                  fallbackSrc={BobbinImage}
                  fieldKey="heroImage"
                  onSave={cms.updateField}
                  sectionKey="hero"
                  value={cms.text("hero", "heroImage", fallback.hero.heroImage)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-12 sm:px-8 lg:py-16">
        <div className="container">
          <CmsEditableList editMode={cms.editMode} fieldKey="items" fields={["title", "text"]} itemName="strength box" label="About strength boxes" onSave={cms.updateField} sectionKey="strengths" value={cmsStrengths} />
          <div className="grid gap-4 md:grid-cols-3">
            {cmsStrengths.map((item, index) => {
              const Icon = typeof item.icon === "function" ? item.icon : iconMap[item.icon] || defaultStrengthIcons[index] || Settings2;

              return (
                <article
                  className="rounded-lg border border-[#3991D5]/10 bg-white p-6 shadow-sm"
                  key={item.title}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#3991D5] text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-5 font-gopher text-xl font-black uppercase text-slate-950">
                    {item.title}
                  </h2>
                  <p className="mt-3 font-pmesans text-sm leading-6 text-slate-600">
                    {item.text}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="mt-10 grid gap-8 rounded-lg border border-[#3991D5]/10 bg-white p-6 shadow-sm lg:grid-cols-[0.9fr_1.1fr] lg:p-8">
            <div className="overflow-hidden rounded-lg bg-[#edf7ff]">
              <CmsEditableImage
                alt="Bobbin USA store and product support"
                className="h-full min-h-[260px] w-full object-cover"
                editMode={cms.editMode}
                fallbackSrc={StoreImage}
                fieldKey="image"
                onSave={cms.updateField}
                sectionKey="what-we-do"
                value={cms.text("what-we-do", "image", fallback["what-we-do"].image)}
              />
            </div>

            <div className="flex flex-col justify-center">
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[#3991D5]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#1278b5]">
                <PackageCheck className="h-4 w-4" />
                <CmsEditableText editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="what-we-do" value={cms.text("what-we-do", "eyebrow", fallback["what-we-do"].eyebrow)} />
              </div>
              <CmsEditableText as="h2" className="mt-4 font-gopher text-3xl font-black uppercase leading-tight text-slate-950 sm:text-4xl" editMode={cms.editMode} fieldKey="headline" multiline onSave={cms.updateField} sectionKey="what-we-do" value={cms.text("what-we-do", "headline", fallback["what-we-do"].headline)} />
              <CmsEditableText as="p" className="mt-4 font-pmesans text-base leading-7 text-slate-600" editMode={cms.editMode} fieldKey="body" multiline onSave={cms.updateField} sectionKey="what-we-do" value={cms.text("what-we-do", "body", fallback["what-we-do"].body)} />

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <CmsEditableList editMode={cms.editMode} fieldKey="values" fields={["value"]} itemName="bullet item" label="About value bullet list" onSave={cms.updateField} sectionKey="what-we-do" value={cmsValues} />
                </div>
                {cmsValues.map((value) => (
                  <div className="flex items-start gap-3" key={value}>
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#3991D5]" />
                    <span className="font-pmesans text-sm leading-6 text-slate-700">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AboutUs;
